"use client";

import { API_ENDPOINTS, buildApiUrl } from "@/services/api-url-service";
import { User } from "@/types/user";
import { getProfilePictureValue } from "@/utils/profile-utils";
import { ApiResponse } from "../model";

type AuthUserData = User[] | User | { user?: User };
type AuthUserResponse = ApiResponse<AuthUserData>;

const SESSION_MIGRATION_REQUIRED = "SESSION_MIGRATION_REQUIRED";

export class SessionMigrationRequiredError extends Error {
  constructor() {
    super("The current session belongs to a previous application client");
    this.name = "SessionMigrationRequiredError";
  }
}

export function isSessionMigrationRequiredError(
  error: unknown,
): error is SessionMigrationRequiredError {
  return error instanceof SessionMigrationRequiredError;
}

export interface GetCurrentUserSessionInput {
  workspaceId?: string;
  organizationId?: string;
  appSessionId?: string;
}
type ActionSourceUserInfo = {
  user_id?: string;
  name?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  picture?: string;
  picture_url?: string;
};
type ActionSourceAccount = {
  user_id?: string;
  username?: string;
  fullname?: string;
};
type ActionSourceRole = {
  role_code?: string;
};
type ActionSourceSession = {
  user_id?: string;
};
type ActionSourceData = {
  user_id?: string;
  user?: ActionSourceUserInfo;
  account?: ActionSourceAccount;
  session?: ActionSourceSession;
  roles?: string[];
  user_role?: ActionSourceRole[];
  apis?: string[];
  permissions?: string[];
  screens?: string[];
  features?: string[];
};

function isSuccessStatus(status?: string): boolean {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === "ok" || normalizedStatus === "success";
}

function cleanStringList(values: string[] | undefined): string[] | undefined {
  if (!values || values.length === 0) {
    return undefined;
  }

  const cleaned = values
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));

  return cleaned.length > 0 ? cleaned : undefined;
}

function isActionSourceData(value: unknown): value is ActionSourceData {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;
  return (
    "user_id" in record ||
    "session" in record ||
    "account" in record ||
    "roles" in record ||
    "user_role" in record ||
    "permissions" in record ||
    "apis" in record ||
    "screens" in record ||
    "features" in record
  );
}

function mapActionSourceToUser(source: ActionSourceData): User {
  const id =
    source.user_id ||
    source.session?.user_id ||
    source.user?.user_id ||
    source.account?.user_id ||
    source.account?.username ||
    source.user?.email ||
    "";

  const roleCodes =
    source.user_role
      ?.map((role) => role?.role_code)
      .filter((value): value is string => Boolean(value)) ?? [];
  const roles = cleanStringList([...roleCodes, ...(source.roles ?? [])]);
  const permissions = cleanStringList([
    ...(source.permissions ?? []),
    ...(source.apis ?? []),
  ]);

  const fullNameFromParts =
    source.user?.first_name || source.user?.last_name
      ? `${source.user?.first_name ?? ''} ${source.user?.last_name ?? ''}`.trim()
      : undefined;

  const flat = source as unknown as Record<string, unknown>;
  const flatEmail = typeof flat.email === 'string' ? flat.email : undefined;
  const flatName = typeof flat.name === 'string' ? flat.name : undefined;
  const profilePicture = getProfilePictureValue(source);

  return {
    id,
    email: source.user?.email || (source.account?.username?.includes('@') ? source.account.username : undefined) || flatEmail,
    name: source.user?.name || source.user?.display_name || source.account?.fullname || fullNameFromParts || flatName,
    avatar: profilePicture,
    picture: profilePicture,
    roles,
    permissions,
    screens: cleanStringList(source.screens),
    features: cleanStringList(source.features),
    user_profile: source.user
      ? {
          user_id: source.user.user_id,
          id: source.user.user_id,
          display_name: source.user.name || source.user.display_name,
          email: source.user.email,
          first_name: source.user.first_name,
          last_name: source.user.last_name,
          picture_url: profilePicture,
        }
      : (id
          ? {
              user_id: id,
              id,
              display_name: flatName,
              email: flatEmail,
              picture_url: profilePicture,
            }
          : undefined),
    account: source.account
      ? {
          user_id: source.account.user_id || id,
          username: source.account.username || source.user?.email || id,
        }
      : undefined,
    apis: cleanStringList(source.apis),
  };
}

function normalizeUserPayload(data: unknown): User | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  if (isActionSourceData(data)) {
    return mapActionSourceToUser(data);
  }

  const record = data as Record<string, unknown>;
  if (typeof record.id === "string") {
    return data as User;
  }

  if ("user" in record && record.user && typeof record.user === "object") {
    const nestedUser = record.user as Record<string, unknown>;
    if (typeof nestedUser.id === "string") {
      const nestedRoles = Array.isArray(nestedUser.roles)
        ? cleanStringList(
            nestedUser.roles.filter(
              (value): value is string => typeof value === "string",
            ),
          )
        : undefined;
      const nestedPermissions = Array.isArray(nestedUser.permissions)
        ? cleanStringList(
            nestedUser.permissions.filter(
              (value): value is string => typeof value === "string",
            ),
          )
        : undefined;
      const nestedScreens = Array.isArray(nestedUser.screens)
        ? cleanStringList(
            nestedUser.screens.filter(
              (value): value is string => typeof value === "string",
            ),
          )
        : undefined;
      const nestedFeatures = Array.isArray(nestedUser.features)
        ? cleanStringList(
            nestedUser.features.filter(
              (value): value is string => typeof value === "string",
            ),
          )
        : undefined;

      const nestedPicture = getProfilePictureValue(nestedUser) || undefined;

      return {
        id: nestedUser.id,
        email:
          typeof nestedUser.email === "string" ? nestedUser.email : undefined,
        name: typeof nestedUser.name === "string" ? nestedUser.name : undefined,
        avatar: nestedPicture,
        picture: nestedPicture,
        roles: nestedRoles,
        permissions: nestedPermissions,
        screens: nestedScreens,
        features: nestedFeatures,
      };
    }

    if (
      typeof nestedUser.user_id === "string" ||
      typeof nestedUser.email === "string"
    ) {
      return {
        id: String(nestedUser.user_id || nestedUser.email || ""),
        email:
          typeof nestedUser.email === "string" ? nestedUser.email : undefined,
        name: typeof nestedUser.name === "string" ? nestedUser.name : undefined,
        avatar: getProfilePictureValue(nestedUser) || undefined,
        picture: getProfilePictureValue(nestedUser) || undefined,
      };
    }
  }

  return null;
}

export function extractUserFromAuthResponse(
  response: AuthUserResponse,
): User | null {
  const { data } = response;

  if (!data) return null;

  if (Array.isArray(data)) {
    return normalizeUserPayload(data[0]);
  }

  return normalizeUserPayload(data);
}

function getStoredSessionToken(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const token =
    localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");

  return token?.trim() || undefined;
}


async function fetchAuthUserFromEndpoint(
  endpoint: string,
  appSessionId?: string,
): Promise<AuthUserResponse> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (appSessionId) {
    (headers as Record<string, string>).Authorization =
      `Bearer ${appSessionId}`;
  }

  const resp = await fetch(endpoint, {
    method: "GET",
    credentials: "include",
    headers,
  });

  const response = (await resp
    .json()
    .catch(() => ({}))) as AuthUserResponse;

  if (
    resp.status === 401 &&
    response.error_code === SESSION_MIGRATION_REQUIRED
  ) {
    throw new SessionMigrationRequiredError();
  }

  if (!resp.ok) {
    throw new Error(`HTTP error! status: ${resp.status}`);
  }

  return response;
}

// SSO Authorization URL service - redirects to localhost auth service
export function authorize(
  input: { next?: string; referer?: string; project_id?: string } = {
    next: "/dashboard",
    project_id: "1",
  },
): void {
  try {
    const param = new URLSearchParams();
    if (input.next) {
      param.set("next", input.next);
    }

    if (input.project_id) {
      param.set("project_id", input.project_id);
    }

    if (input.referer) {
      param.set("referer", input.referer);
    }

    // Build authorization URL pointing to localhost:81
    const authUrl = buildApiUrl(
      API_ENDPOINTS.auth.authorization,
      Object.fromEntries(param),
    );

    // Replace the transient auth bootstrap in history so browser restore/back
    // does not replay one-time SSO callback URLs inside an embedded iframe.
    if (typeof window !== "undefined") {
      console.log("[Auth] Redirecting to:", authUrl);
      window.location.replace(authUrl);
    } else {
      throw new Error("Cannot redirect: window object not available");
    }
  } catch (error) {
    console.error("Error in authorize function:", error);
    throw error;
  }
}

export function logout(input: { post_logout_redirect_uri?: string } = {}) {
  try {
    const param = new URLSearchParams();
    if (input.post_logout_redirect_uri) {
      param.set("post_logout_redirect_uri", input.post_logout_redirect_uri);
    }

    // Build logout URL pointing to localhost:81
    const logoutUrl = buildApiUrl(
      API_ENDPOINTS.auth.logout,
      Object.fromEntries(param),
    );

    // Clear local storage
    if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      console.log("[Auth] Logging out, redirecting to:", logoutUrl);
      window.location.href = logoutUrl;
    } else {
      throw new Error("Cannot redirect: window object not available");
    }
  } catch (error) {
    console.error("Error logout:", error);
    throw error;
  }
}

function buildMeEndpoint(input: GetCurrentUserSessionInput): string {
  const params: Record<string, string> = {};

  if (input.workspaceId?.trim()) {
    params.workspace_id = input.workspaceId.trim();
  }

  if (input.organizationId?.trim()) {
    params.organization_id = input.organizationId.trim();
  }

  if (Object.keys(params).length === 0) {
    return API_ENDPOINTS.auth.me;
  }

  return buildApiUrl(API_ENDPOINTS.auth.me, params);
}

export async function getCurrentUserSession(
  input: GetCurrentUserSessionInput = {},
): Promise<AuthUserResponse> {
  const appSessionId = input.appSessionId?.trim() || getStoredSessionToken();

  // Call both endpoints in parallel:
  // - /auth/me      → screens, roles, permissions
  // - /auth/userinfo → name, email, user profile
  const meEndpoint = buildMeEndpoint(input);

  const [meResult, userinfoResult] = await Promise.allSettled([
    fetchAuthUserFromEndpoint(meEndpoint, appSessionId),
    fetchAuthUserFromEndpoint(API_ENDPOINTS.auth.userinfo, appSessionId),
  ]);

  const migrationResult = [meResult, userinfoResult].find(
    (result) =>
      result.status === "rejected" &&
      isSessionMigrationRequiredError(result.reason),
  );
  if (migrationResult?.status === "rejected") {
    throw migrationResult.reason;
  }

  const meResponse = meResult.status === 'fulfilled' && isSuccessStatus(meResult.value.status) ? meResult.value : null;
  const userinfoResponse = userinfoResult.status === 'fulfilled' && isSuccessStatus(userinfoResult.value.status) ? userinfoResult.value : null;

  if (process.env.NODE_ENV === 'development') {
    console.log('[Auth] /auth/me response:', meResponse);
    console.log('[Auth] /auth/userinfo response:', userinfoResponse);
  }

  if (!meResponse && !userinfoResponse) {
    throw new Error('Failed to fetch current user session');
  }

  // If only one endpoint succeeded, return it directly
  if (!meResponse || !userinfoResponse) {
    return (userinfoResponse ?? meResponse) as AuthUserResponse;
  }

  // Merge at User level (type-safe):
  // - userinfoResponse wins for name/email/profile
  // - meResponse wins for screens/roles/permissions/apis
  const meUser = extractUserFromAuthResponse(meResponse);
  const userinfoUser = extractUserFromAuthResponse(userinfoResponse);

  const mergedUser: User = {
    ...(meUser ?? {}),
    ...(userinfoUser ?? {}),
    screens: meUser?.screens ?? userinfoUser?.screens,
    features: meUser?.features ?? userinfoUser?.features,
    roles: meUser?.roles ?? userinfoUser?.roles,
    permissions: meUser?.permissions ?? userinfoUser?.permissions,
    apis: meUser?.apis ?? userinfoUser?.apis,
    id: meUser?.id || userinfoUser?.id || '',
  };

  return {
    ...userinfoResponse,
    data: mergedUser,
  };
}

export async function refreshSession(): Promise<boolean> {
  try {
    const refreshEndpoint =
      API_ENDPOINTS.auth.refresh ?? API_ENDPOINTS.auth.userinfo;
    const method = API_ENDPOINTS.auth.refresh ? "POST" : "GET";
    const resp = await fetch(refreshEndpoint, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!resp.ok) return false;

    const data = await resp.json().catch(() => ({}) as ApiResponse<User[]>);
    if (data && typeof data === "object" && "status" in data) {
      const status = String(
        (data as { status?: unknown }).status ?? "",
      ).toLowerCase();
      return status === "ok" || status === "success";
    }

    return true;
  } catch (error) {
    console.error("Error refreshing session:", error);
    return false;
  }
}

// Helper function to check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  try {
    const response = await getCurrentUserSession();
    const hasUser = Boolean(extractUserFromAuthResponse(response));
    return isSuccessStatus(response.status) && hasUser;
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
}

// Helper function to get current user
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await getCurrentUserSession();
    if (isSuccessStatus(response.status)) {
      return extractUserFromAuthResponse(response);
    }
    return null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
}
